import React, { useState, useEffect, useRef, useContext } from 'react'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import { useNavigate, useOutletContext } from 'react-router-dom'
import axios from '../../../../config/axios'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import lookupIcon from '../../../../assets/equipment/Tables/lookup.svg'
import pencilIcon from '../../../../assets/equipment/Tables/pencil.svg'
import trashIcon from '../../../../assets/equipment/Tables/trash.svg'
import RegisterFamilyModal from '../RegisterFamilyModal'
import ViewFamilyModal from '../ViewFamilyModal'
import {
  FamiliesContainer,
  TableWrapper,
  MessageContainer,
} from './style'
import {
  GlobalTable,
  HeaderLine,
  Th,
  Tr,
  Td,
  TdButtons,
  DivInTd,
} from '../../../../components/styles/table-style'

import styled from 'styled-components'
import { useTooltip } from '../../../../components/Tooltip/useTooltip'

const CellContainer = styled.div`
  max-width: 220px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const FASHION_API_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

interface FamilyData {
  id: number
  name: string
  collection: string
  familyColor: string
  theme: string
  group: string
  grades: string
  fabrics: string
  trimmings: string
}

const FamiliesTable: React.FC = () => {
  const navigate = useNavigate()
  const { refresh, isLoading: parentLoading, setIsLoading } = useOutletContext<any>()
  const [families, setFamilies] = useState<FamilyData[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [totalFamilies, setTotalFamilies] = useState<number | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [selectedFamily, setSelectedFamily] = useState<FamilyData | null>(null)
  const [deleteFamilyId, setDeleteFamilyId] = useState<number | null>(null)
  const cellRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const { tooltipElement, handleMouseEnter, handleMouseLeave } = useTooltip({
    maxWidth: 360,
    maxHeight: 280,
    rowHeight: 22,
    padding: 16,
    verticalGap: 4,
  })
  const { setFeedback } = useContext(FeedbackContext)

  React.useEffect(() => {
    console.debug('[FamiliesTable] component imports', {
      ViewFamilyModal,
      RegisterFamilyModal,
      LoadMoreButton,
      ConfirmationModal,
    })
  }, [])


  const getFirstWithEllipsis = (content: string) => {
    if (!content) return '-'
    const parts = content.split(',').map(s => s.trim()).filter(Boolean)
    if (parts.length === 0) return '-'
    if (parts.length === 1) return parts[0]
    return `${parts[0]}...`
  }

  const handleCellHover = (e: React.MouseEvent, content: string, key: string, id: number) => {
    const ref = cellRefs.current[`${key}-${id}`]
    if (ref && content) {
      const isOverflowing = ref.scrollWidth > ref.clientWidth
      const items = content.split(',').map(s => s.trim()).filter(Boolean)
      if (isOverflowing || items.length > 1) {
        const tooltipText = items.length > 1 ? items.join('\n') : content
        handleMouseEnter(e, tooltipText)
      }
    }
  }

  const fetchFamilies = async () => {
    setLoading(true)
    if (setIsLoading) setIsLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${FASHION_API_URL}/families`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit: visibleCount,
          offset: 0,
          search: undefined,
        },
      })


      let backendData: any[] = []
      if (Array.isArray(response.data)) {
        backendData = response.data
        setTotalFamilies(undefined)
      } else if (response.data && Array.isArray(response.data.content)) {
        backendData = response.data.content
        const total = response.data.totalElements
        setTotalFamilies(typeof total === 'number' ? total : undefined)
      }

      const fetchedFamilies: FamilyData[] = backendData.map((item: any) => {
        const collectionName = item.Collection?.name || '-'

        const gradeItems = item.GradeInFamily || []
        const gradeNames = gradeItems.map((g: any) => g.Grade?.name).filter(Boolean)
        const gradesStr = gradeNames.length > 0 ? gradeNames.join(', ') : '-'

        const familyMaterials =
          item.FamilyMaterial ||
          item.familyMaterial ||
          item.Materials ||
          item.materials ||
          []
        const fabricNames = familyMaterials
          .map((fm: any) =>
            fm.CollectionMaterial?.Material?.name ||
            fm.CollectionMaterial?.material?.name ||
            fm.Material?.name ||
            fm.name,
          )
          .filter(Boolean)
        const fabricsStr = fabricNames.length > 0 ? fabricNames.join(', ') : '-'

        const byCategory: Record<string, Set<string>> = {}
        familyMaterials.forEach((fm: any) => {
          const resources = [
            ...(fm.FamilyMaterialResources || fm.familyMaterialResources || fm.resources || fm.Resources || []),
            ...(fm.CollectionMaterial?.CollectionMaterialResources || fm.CollectionMaterialResources || []),
          ]


          const resolvedResources: any[] = []
          resources.forEach((r: any) => {
            if (Array.isArray(r.resources)) {
              resolvedResources.push(...r.resources)
            } else {
              resolvedResources.push(r)
            }
          })

          resolvedResources.forEach((fmr: any) => {
            const resource = fmr.Resource || fmr.resource || fmr
            if (!resource) return

            const categoryName =
              resource.Category_Resource?.name ||
              resource.CategoryResource?.name ||
              resource.category?.name ||
              'Sem categoria'

            const materialName =
              resource.Material?.name ||
              resource.Material?.description ||
              resource.material?.name ||
              resource.material?.description ||
              resource.name ||
              resource.description ||
              resource.title ||
              (resource.id ? `Recurso #${resource.id}` : undefined)
            if (!materialName) return

            if (!byCategory[categoryName]) byCategory[categoryName] = new Set()
            byCategory[categoryName].add(materialName)
          })
        })

        const uniqueTrimmings = Array.from(
          new Set(Object.values(byCategory).flatMap((names) => Array.from(names))),
        )
        const trimmingsStr = uniqueTrimmings.length > 0 ? uniqueTrimmings.join(', ') : '-'

        console.debug('[FamiliesTable] computed trimmings', { familyId: item.id, familyMaterials, trimmingsStr })

        const familyColorItems = item.FamilyColor || []
        const familyColorNames = familyColorItems
          .map((fc: any) => fc.CollectionColor?.name || fc.CollectionColorName || fc.name)
          .filter(Boolean)
        const familyColorStr = familyColorNames.length > 0 ? [...new Set(familyColorNames)].join(', ') : '-'

        const familyThemeItems = item.ThemeInFamily || []
        const familyThemeNames = familyThemeItems
          .map((t: any) => t.Theme?.name || t.theme?.name)
          .filter(Boolean)
        const familyThemeStr = familyThemeNames.length > 0 ? [...new Set(familyThemeNames)].join(', ') : '-'

        const groupItems = item.GroupInFamily || []
        const groupNames = groupItems.map((g: any) => g.Group?.name).filter(Boolean)
        const groupsStr = groupNames.length > 0 ? groupNames.join(', ') : '-'

        return {
          id: item.id,
          name: item.name || 'Sem nome',
          collection: collectionName,
          familyColor: familyColorStr,
          theme: familyThemeStr,
          group: groupsStr,
          grades: gradesStr,
          fabrics: fabricsStr,
          trimmings: trimmingsStr,
        }
      })

      fetchedFamilies.sort((a, b) => b.id - a.id)

      setFamilies(fetchedFamilies)
      setLoading(false)
      if (setIsLoading) setIsLoading(false)
    } catch (err: any) {
      console.error('Erro ao buscar famílias:', err)
      setError(
        err.response?.data?.message ||
        'Erro ao carregar famílias. Tente novamente.',
      )
      setLoading(false)
      if (setIsLoading) setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchFamilies()
  }, [refresh, visibleCount])

  const handleView = (family: FamilyData) => {
    setSelectedFamily(family)
    setIsViewModalOpen(true)
  }

  const handleEdit = (family: FamilyData) => {
    setSelectedFamily(family)
    setIsEditModalOpen(true)
  }

  const handleEditFromView = () => {
    setIsViewModalOpen(false)
    setIsEditModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    setDeleteFamilyId(id)
  }

  const handleConfirmDelete = async () => {
    if (!deleteFamilyId) return

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${FASHION_API_URL}/families/${deleteFamilyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      setFeedback({ isOpen: true, statusOk: true, message: 'Família excluída com sucesso!' })
      setDeleteFamilyId(null)
      fetchFamilies()
    } catch (error: any) {
      console.error('Erro ao excluir família:', error)
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: error.response?.data?.message || 'Erro ao excluir família. Tente novamente.',
      })
    }
  }

  const handleCancelDelete = () => {
    setDeleteFamilyId(null)
  }

  const handleModalClose = () => {
    setIsEditModalOpen(false)
    setIsViewModalOpen(false)
    setSelectedFamily(null)
    fetchFamilies()
  }

  const canRenderViewFamilyModal = typeof ViewFamilyModal === 'function'
  const canRenderRegisterFamilyModal = typeof RegisterFamilyModal === 'function'
  const canRenderLoadMoreButton = typeof LoadMoreButton === 'function'
  const canRenderConfirmationModal = typeof ConfirmationModal === 'function'

  if (loading) {
    return (
      <FamiliesContainer>
        <MessageContainer>
          <p>Carregando famílias...</p>
        </MessageContainer>
      </FamiliesContainer>
    )
  }

  if (error) {
    return (
      <FamiliesContainer>
        <MessageContainer style={{ color: 'red' }}>
          <p>{error}</p>
        </MessageContainer>
      </FamiliesContainer>
    )
  }

  if (families.length === 0) {
    return (
      <FamiliesContainer>
        <MessageContainer>
          <p>Nenhuma família encontrada.</p>
        </MessageContainer>
      </FamiliesContainer>
    )
  }

  return (
    <FamiliesContainer>
      <TableWrapper>
        <GlobalTable>
          <thead>
            <HeaderLine>
              <Th>Família</Th>
              <Th>Coleção</Th>
              <Th>Cor</Th>
              <Th>Tema</Th>
              <Th>Grupo</Th>
              <Th>Grades</Th>
              <Th>Tecidos</Th>
              <Th>Aviamentos</Th>
              <Th></Th>
            </HeaderLine>
          </thead>
          <tbody>
            {families.map((family) => (
              <Tr key={family.id}>
                <Td>{family.name}</Td>
                <Td>{family.collection}</Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`familyColor-${family.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, family.familyColor, 'familyColor', family.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(family.familyColor)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`theme-${family.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, family.theme, 'theme', family.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(family.theme)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`group-${family.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, family.group, 'group', family.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(family.group)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`grades-${family.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, family.grades, 'grades', family.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(family.grades)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`fabrics-${family.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, family.fabrics, 'fabrics', family.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(family.fabrics)}
                  </CellContainer>
                </Td>
                <Td>
                  <CellContainer
                    ref={(el) => (cellRefs.current[`trimmings-${family.id}`] = el)}
                    onMouseEnter={(e: any) => handleCellHover(e, family.trimmings, 'trimmings', family.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {getFirstWithEllipsis(family.trimmings)}
                  </CellContainer>
                </Td>
                <TdButtons>
                  <DivInTd className='hover-blue' onClick={() => handleView(family)} title='Visualizar'>
                    <img src={lookupIcon} alt='Visualizar' />
                  </DivInTd>
                  <DivInTd className='hover-blue' onClick={() => handleEdit(family)} title='Editar'>
                    <img src={pencilIcon} alt='Editar' />
                  </DivInTd>
                  <DivInTd className='hover-red' onClick={() => handleDelete(family.id)} title='Excluir'>
                    <img src={trashIcon} alt='Excluir' />
                  </DivInTd>
                </TdButtons>
              </Tr>
            ))}
          </tbody>
        </GlobalTable>
        {canRenderLoadMoreButton ? (
          <LoadMoreButton
            currentCount={families.length}
            totalCount={totalFamilies}
            hasMore={typeof totalFamilies === 'number' ? families.length < totalFamilies : false}
            onLoadMore={() => setVisibleCount((prev) => prev + 50)}
          />
        ) : null}
      </TableWrapper>

      {tooltipElement}

      {canRenderViewFamilyModal && selectedFamily ? (
        <ViewFamilyModal
          isOpen={isViewModalOpen}
          onClose={handleModalClose}
          onEdit={handleEditFromView}
          familyId={selectedFamily.id}
        />
      ) : null}

      {canRenderRegisterFamilyModal && selectedFamily ? (
        <RegisterFamilyModal
          isOpen={isEditModalOpen}
          onClose={handleModalClose}
          onSuccess={(saved) => {
            fetchFamilies()

            if (saved && selectedFamily && saved.id === selectedFamily.id) {
              setSelectedFamily(saved)
            }
          }}
          mode="edit"
          familyId={selectedFamily.id}
        />
      ) : null}

      {canRenderConfirmationModal ? (
        <ConfirmationModal
          isOpen={deleteFamilyId !== null}
          title='Confirmar exclusão'
          message='Tem certeza que deseja excluir esta família? Esta ação não pode ser desfeita.'
          actions={[
            { label: 'Cancelar', onClick: handleCancelDelete, variant: 'outline' },
            { label: 'Excluir', onClick: handleConfirmDelete, variant: 'danger' },
          ]}
        />
      ) : null}
    </FamiliesContainer>
  )
}

export default FamiliesTable